import { useParams, Link } from 'react-router-dom';
import { animeSeries } from '@/mocks/anime';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/feature/AnimeCard';

export default function AnimeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const anime = animeSeries.find((a) => a.slug === slug);

  if (!anime) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 pt-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
          <h1 className="font-heading text-xl font-bold text-foreground-900">没有找到这部作品</h1>
          <p className="text-sm text-foreground-600">该作品的分集剧情可能尚未收录，去看看其他热门番吧。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" /> 返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = animeSeries.filter((a) => a.slug !== anime.slug).slice(0, 4);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${anime.title} 分集剧情解说`,
    description: `${anime.title}的分集剧情与每话简介、剧情梳理、故事线解说。`,
    datePublished: `${anime.year}-01-01`,
    inLanguage: 'zh-CN',
    author: { '@type': 'Organization', name: '羞羞动漫网' },
    publisher: { '@type': 'Organization', name: '羞羞动漫网' },
    mainEntityOfPage: `https://laughland3.github.io/anime/${anime.slug}`,
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://laughland3.github.io/' },
      { '@type': 'ListItem', position: 2, name: anime.title, item: `https://laughland3.github.io/anime/${anime.slug}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <Navbar />

      {/* breadcrumb */}
      <div className="mx-auto max-w-7xl px-4 pt-20 md:px-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 pt-3 text-xs text-foreground-600">
          <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-primary-600">
            <i className="ri-home-4-line" /> 首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-900">{anime.title} · 分集剧情</span>
        </nav>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        {/* header */}
        <section className="grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7 lg:grid-cols-3">
          <div className="overflow-hidden rounded-lg bg-background-200 lg:col-span-1">
            <img
              src={anime.cover}
              alt={`${anime.title}动漫分集剧情封面`}
              title={`${anime.title} 分集剧情`}
              className="aspect-[4/5] w-full object-cover object-top"
            />
          </div>
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              {anime.genre.map((g) => (
                <span key={g} className="inline-flex items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {g}
                </span>
              ))}
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${anime.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-background-200 text-foreground-700'}`}>
                {anime.status}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">{anime.title}</h1>
            <p className="mt-2 text-base text-foreground-700">{anime.tagline}</p>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">{anime.intro}</p>

            <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { icon: 'ri-calendar-line', label: '年份', value: `${anime.year}` },
                { icon: 'ri-community-line', label: '制作', value: anime.studio },
                { icon: 'ri-star-fill', label: '评分', value: anime.rating.toFixed(1) },
                { icon: 'ri-clapperboard-line', label: '话数', value: `${anime.episodesCount} 话` },
              ].map((m) => (
                <div key={m.label} className="rounded-md bg-background-100 p-3">
                  <dt className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className={`${m.icon} text-primary-500`} /> {m.label}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-foreground-900">{m.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* content: episodes + sidebar */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* episode list */}
          <section className="lg:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {anime.title} · 每话简介
              </h2>
              <span className="text-xs text-foreground-600">共 {anime.episodesCount} 话</span>
            </div>
            <p className="mt-2 text-sm text-foreground-700">按话整理，逐话对照剧情，帮你在补番、追更、回顾时快速定位。</p>

            <div className="mt-6 space-y-3">
              {anime.episodes.map((ep) => (
                <article key={ep.num} className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5">
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {ep.num}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 md:text-base">
                        第 {ep.num} 话 · {ep.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-7 text-foreground-700">{ep.summary}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* sidebar */}
          <aside className="space-y-6">
            {/* storyline */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-list-unordered text-primary-500" /> 剧情梳理 · 故事线
              </h2>
              <p className="mt-2 text-sm text-foreground-700">把整部番的主线归纳成几个阶段，快速掌握走向。</p>
              <ol className="mt-5 space-y-4 border-l-2 border-background-200 pl-5">
                {anime.storyline.map((point, i) => (
                  <li key={i} className="relative">
                    <span className={`absolute -left-[27px] flex h-3.5 w-3.5 items-center justify-center rounded-full ring-4 ring-background-50 ${i === 0 ? 'bg-accent-500' : 'bg-primary-400'}`} />
                    <p className="text-xs font-bold uppercase tracking-wide text-foreground-600">第 {i + 1} 幕</p>
                    <p className="mt-1 text-sm leading-6 text-foreground-800">{point}</p>
                  </li>
                ))}
              </ol>
            </div>

            {/* guide card */}
            <div className="rounded-lg bg-foreground-950 p-5">
              <h2 className="font-heading text-lg font-bold text-background-50">怎么用这份分集简介？</h2>
              <ul className="mt-4 space-y-3 text-sm text-background-200">
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-checkbox-circle-line text-accent-400" /> 补番：先看剧情梳理，再按话对照简介。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-checkbox-circle-line text-accent-400" /> 追更：用最新话简介快速确认剧情走向。
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="mt-0.5 ri-checkbox-circle-line text-accent-400" /> 回顾：跳转到想找的伏笔与转折对应话数。
                </li>
              </ul>
            </div>
          </aside>
        </div>

        {/* related */}
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">猜你还想看</h2>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
            {related.map((r) => (
              <AnimeCard key={r.slug} anime={r} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}